import { useMemo, useState } from "react";
import { useGetIdentity } from "@refinedev/core";
import { Alert, App, Button, Card, Input, Modal, Space, Table, Typography } from "antd";
import type { AdminInfo } from "../../providers/authProvider";
import { adminFetch, errorMessage } from "../../lib/adminApi";
import { useSupabaseUsers } from "../../lib/useSupabaseUsers";
import { ActiveSubscriptionSwitch } from "../../components";
import type { SupabaseUserOption } from "../../types/filterCopy";

const { Title, Paragraph, Text } = Typography;

type DeleteUserResponse = {
  success: boolean;
  deleted_user_id: string;
  deleted: {
    searches: number;
    conversions: number;
    opensearch_subscriptions: number;
    application_data: {
      saved_filters: number;
      notification_subscriptions: number;
    };
  };
};

export const UsersList = () => {
  const { notification } = App.useApp();
  const { data: identity } = useGetIdentity<AdminInfo>();
  const [activeOnly, setActiveOnly] = useState(true);
  const { users, setUsers, loading } = useSupabaseUsers(activeOnly);
  const [query, setQuery] = useState("");
  const [selected, setSelected] = useState<SupabaseUserOption>();
  const [confirmation, setConfirmation] = useState("");
  const [deleting, setDeleting] = useState(false);

  const visibleUsers = useMemo(() => {
    const needle = query.trim().toLocaleLowerCase("pl");
    if (!needle) return users;
    return users.filter(
      (user) =>
        user.email.toLocaleLowerCase("pl").includes(needle) ||
        user.id.toLocaleLowerCase("pl").includes(needle),
    );
  }, [query, users]);

  const closeModal = () => {
    if (deleting) return;
    setSelected(undefined);
    setConfirmation("");
  };

  const deleteUser = async () => {
    const expectedConfirmation = selected?.email || selected?.id || "";
    if (
      !selected ||
      confirmation.trim().toLocaleLowerCase() !== expectedConfirmation.toLocaleLowerCase()
    ) {
      return;
    }
    setDeleting(true);
    try {
      const response = await adminFetch<DeleteUserResponse>(
        `supabase/users/${encodeURIComponent(selected.id)}`,
        {
          method: "DELETE",
          body: JSON.stringify({ confirmation: confirmation.trim() }),
        },
      );
      setUsers((current) => current.filter((user) => user.id !== selected.id));
      notification.success({
        message: "Konto zostało usunięte",
        description: `Usunięto ${response.deleted.application_data.saved_filters} filtrów, ${response.deleted.application_data.notification_subscriptions} subskrypcji i ${response.deleted.searches} wyszukiwań.`,
      });
      setSelected(undefined);
      setConfirmation("");
    } catch (error) {
      notification.error({
        message: "Nie udało się usunąć konta",
        description: errorMessage(error),
        duration: 8,
      });
    } finally {
      setDeleting(false);
    }
  };

  const confirmationMatches = Boolean(
    selected &&
      confirmation.trim().toLocaleLowerCase() ===
        (selected.email || selected.id).toLocaleLowerCase(),
  );

  return (
    <Card>
      <Title level={4}>Użytkownicy</Title>
      <Paragraph type="secondary">
        Usunięcie konta jest trwałe. Razem z loginem usuwane są zapisane filtry,
        subskrypcje powiadomień o nowych przetargach, ustawienia raportów oraz historia
        wyszukiwań i zdarzeń tego użytkownika.
      </Paragraph>

      <Space wrap size={16} style={{ marginBottom: 16 }}>
        <Input.Search
          allowClear
          placeholder="Szukaj po adresie e-mail lub ID"
          value={query}
          onChange={(event) => setQuery(event.target.value)}
          style={{ width: 440, maxWidth: "100%" }}
        />
        <ActiveSubscriptionSwitch checked={activeOnly} onChange={setActiveOnly} />
      </Space>

      <Table
        rowKey="id"
        loading={loading}
        dataSource={visibleUsers}
        pagination={{ pageSize: 25, showSizeChanger: true }}
        locale={{ emptyText: "Brak użytkowników." }}
        columns={[
          {
            title: "E-mail",
            dataIndex: "email",
            render: (email: string) => email || <Text type="secondary">brak adresu</Text>,
          },
          {
            title: "ID",
            dataIndex: "id",
            render: (id: string) => <Text copyable code>{id}</Text>,
          },
          {
            title: "",
            key: "actions",
            width: 120,
            render: (_: unknown, user: SupabaseUserOption) => (
              <Button
                danger
                disabled={user.id === identity?.id}
                title={user.id === identity?.id ? "Nie możesz usunąć aktywnego konta" : undefined}
                onClick={() => {
                  setSelected(user);
                  setConfirmation("");
                }}
              >
                Usuń konto
              </Button>
            ),
          },
        ]}
      />

      <Modal
        open={Boolean(selected)}
        title="Trwałe usunięcie konta"
        okText="Usuń konto i dane"
        okButtonProps={{ danger: true, disabled: !confirmationMatches }}
        confirmLoading={deleting}
        cancelText="Anuluj"
        closable={!deleting}
        maskClosable={!deleting}
        onCancel={closeModal}
        onOk={deleteUser}
      >
        {selected && (
          <Space direction="vertical" size="middle" style={{ width: "100%" }}>
            <Alert
              type="error"
              showIcon
              message="Tej operacji nie można cofnąć"
              description="Zniknie konto Supabase Auth oraz dane filtrów i powiadomień używane przez search engine."
            />
            <Paragraph style={{ marginBottom: 0 }}>
              Aby potwierdzić usunięcie, wpisz {selected.email ? "adres" : "ID"}{" "}
              <Text strong>{selected.email || selected.id}</Text>.
            </Paragraph>
            <Input
              autoFocus
              value={confirmation}
              onChange={(event) => setConfirmation(event.target.value)}
              placeholder={selected.email || selected.id}
              status={confirmation && !confirmationMatches ? "error" : undefined}
            />
          </Space>
        )}
      </Modal>
    </Card>
  );
};
